// src/MensajesContext.js
import React, { createContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { API_URL } from "../config";
import { isPublicPath } from "../utils/publicPaths";
import { fetchSessionStatus } from "../utils/sessionAuth";
import { canLoadMessagingContacts } from "../utils/messagingContactsLoadPolicy";
import {
  MESSAGING_POLL_INTERVAL_MS,
  MESSAGING_POLL_INTERVAL_HIDDEN_MS,
  MESSAGING_UNREAD_POLL_PATH,
  nextMessagingPollBackoffMs,
} from "../utils/messagingPollConfig";
import { areUnreadByContactEqual } from "../utils/unreadByContactEquality";
import { APP_BRAND_NAME } from "../constants/appBrand";

export const MensajesContext = createContext();


// Let's use a real short beep base64 or just rely on Browser Notification. 
// I will use a simple reliable external URL or just Notification API for now to avoid huge base64 strings.
// Actually, a very short beep is better.
const BEEP_URL = "https://actions.google.com/sounds/v1/alarms/beep_short.ogg";
const MESSAGING_FORBIDDEN_KEY = "mensajes_forbidden";
const IS_MESSAGING_CONTACTS_DEBUG = process.env.NODE_ENV === "development";

export function normalizeUnreadPayload(payload) {
  const hasByContact = Array.isArray(payload?.byContact);
  const byContact = hasByContact ? payload.byContact : [];
  const rawCount = typeof payload?.count === "number" ? payload.count : 0;
  const countFromContacts = byContact.reduce(
    (sum, row) => sum + (Number(row?.count) || 0),
    0,
  );
  return {
    count: hasByContact ? countFromContacts : rawCount,
    byContact,
  };
}

function getUserId() {
  if (typeof localStorage === "undefined") return "anon";
  return localStorage.getItem("idUsuario") || "anon";
}

function getStorageKey() {
  return `${MESSAGING_FORBIDDEN_KEY}_${getUserId()}`;
}

export function MensajesProvider({ children }) {
  const location = useLocation();
  const pathname = location.pathname;
  const cleanPath = pathname.endsWith("/") && pathname.length > 1 ? pathname.slice(0, -1) : pathname;
  const isPublic = isPublicPath(cleanPath);
  const [contactos, setContactos] = useState([]);
  const [filtro, setFiltro] = useState("administradores");
  const [contactosFiltroActual, setContactosFiltroActual] = useState("");
  const [contactosLoading, setContactosLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByContact, setUnreadByContact] = useState([]);
  const unreadByContactRef = useRef([]);
  const lastUnreadRef = useRef(0);
  const pollBackoffUntilRef = useRef(0);
  const pollBackoffDelayRef = useRef(0);
  const [pageVisible, setPageVisible] = useState(
    typeof document === "undefined" ? true : document.visibilityState !== "hidden",
  );
  const messagingForbiddenRef = useRef({ userId: null, forbidden: false });
  const [messagingPermission, setMessagingPermission] = useState(null);
  const messagingPermissionRef = useRef(null);
  const [hasLegacyThreads, setHasLegacyThreads] = useState(false);
  const hasLegacyThreadsRef = useRef(false);
  const cargarContactosSeqRef = useRef(0);
  const contactosRequestCacheRef = useRef(new Map());
  const contactosInflightRef = useRef(new Map());

  const isMessagingForbidden = () => {
    const uid = getUserId();
    const ref = messagingForbiddenRef.current;
    if (ref.userId !== uid) {
      ref.userId = uid;
      ref.forbidden =
        typeof sessionStorage !== "undefined" && sessionStorage.getItem(getStorageKey()) === "1";
    }
    return ref.forbidden;
  };

  const setMessagingForbidden = useCallback((forbidden) => {
    const uid = getUserId();
    const prevUid = messagingForbiddenRef.current.userId;
    messagingForbiddenRef.current = { userId: uid, forbidden };
    try {
      if (prevUid && prevUid !== uid) {
        sessionStorage.removeItem(`${MESSAGING_FORBIDDEN_KEY}_${prevUid}`);
      }
      const key = `${MESSAGING_FORBIDDEN_KEY}_${uid}`;
      if (forbidden) {
        sessionStorage.setItem(key, "1");
      } else {
        sessionStorage.removeItem(key);
      }
    } catch { /* ignore */ }
  }, []);

  const updateTitle = useCallback((count) => {
    document.title = count > 0 ? `(${count}) ${APP_BRAND_NAME}` : APP_BRAND_NAME;
  }, []);

  const applyUnreadPayload = useCallback((payload) => {
    const { count, byContact } = normalizeUnreadPayload(payload);
    lastUnreadRef.current = count;
    unreadByContactRef.current = byContact;
    setTotalUnread(count);
    setUnreadByContact((prev) => (areUnreadByContactEqual(prev, byContact) ? prev : byContact));
    updateTitle(count);
  }, [updateTitle]);

  const clearMessagingState = useCallback(() => {
    setContactos([]);
    setContactosFiltroActual("");
    setContactosLoading(false);
    lastUnreadRef.current = 0;
    setTotalUnread(0);
    unreadByContactRef.current = [];
    setUnreadByContact((prev) => (prev.length === 0 ? prev : []));
    updateTitle(0);
  }, [updateTitle]);

  const acknowledgeUnreadForContact = useCallback(async () => {
    if (isPublic) {
      return;
    }
    if (isMessagingForbidden()) {
      clearMessagingState();
      return;
    }
    const canPollUnread =
      messagingPermissionRef.current === true || hasLegacyThreadsRef.current === true;
    if (!canPollUnread) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}${MESSAGING_UNREAD_POLL_PATH}`, { credentials: "include" });
      if (res.ok) {
        applyUnreadPayload(await res.json());
      }
    } catch {
      /* ignore */
    }
  }, [applyUnreadPayload, clearMessagingState, isPublic]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibility = () => {
      setPageVisible(document.visibilityState !== "hidden");
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const markMessagingForbidden = useCallback(() => {
    setMessagingForbidden(true);
    clearMessagingState();
  }, [clearMessagingState, setMessagingForbidden]);

  useEffect(() => {
    if ("Notification" in globalThis && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const playNotificationSound = () => {
    try {
      const audio = new Audio(BEEP_URL);
      audio.volume = 0.5;
      audio.play().catch(e => console.warn("Audio play blocked", e));
    } catch (e) {
      console.warn("Audio error", e);
    }
  };

  const showBrowserNotification = (count) => {
    if ("Notification" in globalThis && Notification.permission === "granted") {
      new Notification(`${APP_BRAND_NAME} - Nuevo mensaje`, {
        body: `Tienes ${count} mensaje(s) sin leer.`,
        icon: "/logos/LOGO_ACADEMIA.png"
      });
    }
  };

  // Reset permission when navigating to public path so we re-check when entering private again (e.g. after login)
  useEffect(() => {
    if (isPublic) {
      setMessagingPermission(null);
      messagingPermissionRef.current = null;
      return;
    }
    if (messagingPermission !== null) return;
    let cancelled = false;
    fetchSessionStatus()
      .then((session) => {
        if (cancelled) return;
        if (!session.authenticated) {
          messagingPermissionRef.current = false;
          setMessagingPermission(false);
          markMessagingForbidden();
          return;
        }
        const can = !!session.usuario?.puedeMensajear;
        messagingPermissionRef.current = can;
        setMessagingPermission(can);
        if (!can) markMessagingForbidden();
      })
      .catch(() => {
        if (!cancelled) {
          /* leave permission null on network error so we retry on next navigation */
        }
      });
    return () => { cancelled = true; };
  }, [pathname, messagingPermission, markMessagingForbidden, isPublic]);

  useEffect(() => {
    if (isPublic) {
      setHasLegacyThreads(false);
      hasLegacyThreadsRef.current = false;
      return undefined;
    }
    if (messagingPermissionRef.current === true) {
      setHasLegacyThreads(false);
      hasLegacyThreadsRef.current = false;
      return undefined;
    }
    if (messagingPermissionRef.current === false) {
      let cancelled = false;
      fetch(`${API_URL}/mensajes/has-active-threads`, { credentials: "include" })
        .then((res) => (res.ok ? res.json() : { hasActive: false }))
        .then((data) => {
          if (cancelled) return;
          const active = !!data?.hasActive;
          hasLegacyThreadsRef.current = active;
          setHasLegacyThreads(active);
          if (active) {
            setMessagingForbidden(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            hasLegacyThreadsRef.current = false;
            setHasLegacyThreads(false);
          }
        });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [pathname, messagingPermission, isPublic, setMessagingForbidden]);

  const pollGlobalUnread = useCallback(async () => {
    if (isPublic) {
      setMessagingForbidden(false);
      if (lastUnreadRef.current !== 0) {
        applyUnreadPayload({ count: 0, byContact: [] });
      }
      return;
    }
    if (Date.now() < pollBackoffUntilRef.current) return;
    const canPollUnread =
      messagingPermissionRef.current === true || hasLegacyThreadsRef.current === true;
    if (!canPollUnread) return;
    if (isMessagingForbidden()) {
      clearMessagingState();
      return;
    }
    try {
      const res = await fetch(`${API_URL}${MESSAGING_UNREAD_POLL_PATH}`, { credentials: "include" });
      if (res.status === 403) {
        markMessagingForbidden();
        return;
      }
      if (res.status === 429 || res.status === 503) {
        const delayMs = nextMessagingPollBackoffMs(pollBackoffDelayRef.current);
        pollBackoffDelayRef.current = delayMs;
        pollBackoffUntilRef.current = Date.now() + delayMs;
        return;
      }
      if (res.ok) {
        pollBackoffUntilRef.current = 0;
        pollBackoffDelayRef.current = 0;
        const data = await res.json();
        const count = typeof data.count === "number" ? data.count : 0;
        const byContact = Array.isArray(data.byContact) ? data.byContact : [];
        if (count > lastUnreadRef.current) {
          playNotificationSound();
          showBrowserNotification(count);
        }
        applyUnreadPayload({ count, byContact });
      }
    } catch (_) {
      /* ignore */
    }
  }, [applyUnreadPayload, clearMessagingState, markMessagingForbidden, isPublic, setMessagingForbidden]);

  useEffect(() => {
    if (isPublic) {
      pollGlobalUnread();
      return undefined;
    }
    const canPoll = messagingPermission === true || hasLegacyThreads;
    if (!canPoll) return undefined;
    const intervalMs = pageVisible
      ? MESSAGING_POLL_INTERVAL_MS
      : MESSAGING_POLL_INTERVAL_HIDDEN_MS;
    pollGlobalUnread();
    const id = setInterval(pollGlobalUnread, intervalMs);
    return () => clearInterval(id);
  }, [isPublic, messagingPermission, hasLegacyThreads, pollGlobalUnread, pageVisible]);


const cargarContactos = useCallback(async (filtroCurso) => {
    if (isPublic) {
      setMessagingForbidden(false);
      if (IS_MESSAGING_CONTACTS_DEBUG) {
        console.log("[MensajesContext] cargarContactos abort: ruta pública");
      }
      return;
    }
    if (IS_MESSAGING_CONTACTS_DEBUG) {
      console.log("[MensajesContext] cargarContactos entrada", {
        filtroCurso,
        messagingPermissionRef: messagingPermissionRef.current,
        isPublic,
        messagingForbidden: isMessagingForbidden(),
      });
    }
    if (
      !canLoadMessagingContacts(
        messagingPermissionRef.current,
        hasLegacyThreadsRef.current
      )
    ) {
      if (IS_MESSAGING_CONTACTS_DEBUG) {
        console.log("[MensajesContext] cargarContactos abort: ref permiso", messagingPermissionRef.current);
      }
      return;
    }

    const filtroNormalizado = String(filtroCurso ?? "").trim();
    const url = `${API_URL}/mensajes/contactos?filtro=${encodeURIComponent(filtroNormalizado)}`;
    const seq = ++cargarContactosSeqRef.current;
    const cachedContactos = contactosRequestCacheRef.current.get(filtroNormalizado);
    if (cachedContactos && cachedContactos.expiresAt > Date.now()) {
      setContactos(cachedContactos.data);
      setContactosFiltroActual(filtroNormalizado);
      setContactosLoading(false);
      return;
    }
    setContactosLoading(true);
    if (IS_MESSAGING_CONTACTS_DEBUG) {
      console.log("[MensajesContext] cargarContactos fetch", { url, seq });
    }

    try {
     const request =
       contactosInflightRef.current.get(filtroNormalizado) ||
       fetch(url, { credentials: "include" })
         .then(async (res) => {
           let body = null;
           try {
             body = await res.json();
           } catch {
             body = null;
           }
           return { ok: res.ok, status: res.status, body };
         })
         .finally(() => {
           contactosInflightRef.current.delete(filtroNormalizado);
         });
     contactosInflightRef.current.set(filtroNormalizado, request);
     const result = await request;
     if (result.status === 403) {
       const body = result.body && typeof result.body === "object" ? result.body : {};
       const msg = typeof body.error === "string" ? body.error : "";
       if (msg.includes("este filtro")) {
         if (seq === cargarContactosSeqRef.current) {
           setContactos([]);
           setContactosFiltroActual(filtroNormalizado);
         }
         return;
       }
       markMessagingForbidden();
       return;
     }
     if (!result.ok) {
       if (result.status === 401) {
         clearMessagingState();
         return;
       }
       throw new Error("Error cargando contactos");
     }
    const data = result.body;
    const contactosList = Array.isArray(data) ? data : [];
    contactosRequestCacheRef.current.set(filtroNormalizado, {
      data: contactosList,
      expiresAt: Date.now() + 5000,
    });
    if (seq === cargarContactosSeqRef.current) {
      setContactos(contactosList);
      setContactosFiltroActual(filtroNormalizado);
    }
   } catch (_) {
     /* ignore */
   } finally {
     if (seq === cargarContactosSeqRef.current) {
       setContactosLoading(false);
     }
   }
 }, [clearMessagingState, markMessagingForbidden, isPublic, setMessagingForbidden]);

  // 1) reload when filter changes (full permission or legacy threads; empty filtro = no course scope)
  useEffect(() => {
    if (
      canLoadMessagingContacts(messagingPermission, hasLegacyThreads) &&
      String(filtro ?? "").trim() !== ""
    ) {
      cargarContactos(filtro);
    }
  }, [filtro, messagingPermission, hasLegacyThreads, cargarContactos]);

  const contextValue = useMemo(
    () => ({
      contactos,
      contactosFiltroActual,
      contactosLoading,
      setContactos,
      filtro,
      setFiltro,
      cargarContactos,
      totalUnread,
      unreadByContact,
      acknowledgeUnreadForContact,
      messagingPermission,
    }),
    [contactos, contactosFiltroActual, contactosLoading, filtro, cargarContactos, totalUnread, unreadByContact, acknowledgeUnreadForContact, messagingPermission]
  );

  return (
    <MensajesContext.Provider value={contextValue}>
      {children}
    </MensajesContext.Provider>
  );
}

MensajesProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
